import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  MoreVertical, Eye, Download, CheckCircle, RotateCcw, 
  FileSearch, Trash2, ArrowRight, RefreshCw, History, X
} from 'lucide-react';

export function isFileUnavailableForInspection(file = {}) {
  const assessment = file.retention_assessment || {};
  const availability = assessment.storage_availability || file.storage_availability;
  const referenceState = assessment.storage_reference_state || file.storage_reference_state;
  return Boolean(
    file.file_unavailable ||
    file.bytes_unavailable ||
    availability === 'retired' ||
    availability === 'legacy_storage_retired' ||
    availability === 'unavailable' ||
    availability === 'retrieval_failed' ||
    referenceState === 'retired_emergent_reference'
  );
}

/**
 * Pure, testable availability of per-file document actions given ONLY the file
 * state (no handler wiring). This encodes the acceptance/rejection truth rules so
 * every UI surface derives the same answer:
 *   - A verified file must NOT offer ordinary Verify/Reject or one-click stamp removal.
 *     A deliberate, reasoned amendment/replacement flow owns reopening final evidence.
 *   - Files that are unavailable for inspection or no longer active expose no mutations.
 */
export function getDocumentActionAvailability(file = {}, { isAuditor = false } = {}) {
  // Exclude "not_verified" placeholder which is NOT an actual stamp.
  const hasValidStamp = file.verification_stamp &&
    file.verification_stamp !== 'not_verified' &&
    file.verification_stamp !== '';
  const isVerified = Boolean(file.verified || hasValidStamp || file.stamped_file_url);
  const hasExtraction = file.extraction_status?.status === 'awaiting_review';
  const isActiveFile = !['superseded', 'uploaded_in_error', 'rejected', 'deleted'].includes(file.status);
  const inspectionUnavailable = isFileUnavailableForInspection(file);
  const backendRequiresReplacement = file.retention_assessment?.replacement_state === 'replacement_required';

  return {
    isVerified,
    hasExtraction,
    isActiveFile,
    inspectionUnavailable,
    backendRequiresReplacement,
    // Ordinary review controls only exist while the active version is NOT yet verified.
    canVerify: !inspectionUnavailable && !isVerified && isActiveFile && !isAuditor,
    canRejectEvidence: !inspectionUnavailable && !isVerified && isActiveFile && !isAuditor,
    // One-click stamp removal is intentionally not exposed for finalised evidence.
    canRemoveStamp: false,
    // Amendment path (worker re-upload) stays available; unavailable files only via backend flag.
    canRequestReplacement: !isAuditor && isActiveFile && (!inspectionUnavailable || backendRequiresReplacement),
  };
}

/**
 * DocumentActionMenu - Per-file action dropdown menu
 * 
 * Simplified UX following 5 E's of Usability:
 * - Primary actions: View, Download (always visible)
 * - Verification: Verify OR Request Replacement (mutually exclusive states)
 * - Admin Actions: Grouped under "More Actions" to reduce clutter
 */
export default function DocumentActionMenu({
  file,
  onView,
  onDownload,
  onVerify,
  onRequestReplacement,
  onRejectEvidence,
  onReject,
  onExtractReview,
  onMarkUploadedInError,
  onSupersede,
  onMoveCategory,
  onViewHistory,
  isAuditor = false,
  isProcessing = false
}) {
  // Simplified state checks — derived from the shared, testable availability helper
  // so this menu agrees with every other surface on the acceptance/rejection truth.
  const avail = getDocumentActionAvailability(file, { isAuditor });
  const { isVerified, hasExtraction, isActiveFile, inspectionUnavailable, backendRequiresReplacement } = avail;

  // Determine primary action based on file state
  const showVerifyAction = avail.canVerify;
  const canRequestReplacement = avail.canRequestReplacement && (
    inspectionUnavailable ? !!onRequestReplacement : !!(onRequestReplacement || onReject)
  );
  // Ordinary "Reject Evidence" must disappear once the active version is verified.
  const canRejectEvidence = avail.canRejectEvidence && !!(onRejectEvidence || onReject);
  const canMarkUploadedInError = !isAuditor && isActiveFile && !!onMarkUploadedInError && (
    !inspectionUnavailable || file.allow_uploaded_in_error_when_unavailable
  );
  const canUseAdvancedMutations = !inspectionUnavailable && !isAuditor && isActiveFile && (onSupersede || onMoveCategory);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0"
          disabled={isProcessing}
          data-testid={`file-action-menu-${file.file_id}`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {/* Primary Actions - Always visible */}
        {!inspectionUnavailable && onView && (
          <DropdownMenuItem onClick={onView} data-testid="action-view">
            <Eye className="h-4 w-4 mr-2" />
            View File
          </DropdownMenuItem>
        )}
        {!inspectionUnavailable && onDownload && (
          <DropdownMenuItem onClick={onDownload} data-testid="action-download">
            <Download className="h-4 w-4 mr-2" />
            Download
          </DropdownMenuItem>
        )}

        {/* Extraction Review - High priority if pending */}
        {!inspectionUnavailable && hasExtraction && onExtractReview && !isAuditor && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onExtractReview} data-testid="action-extract-review">
              <FileSearch className="h-4 w-4 mr-2 text-purple-600" />
              <span className="text-purple-600 font-medium">Review Extraction</span>
            </DropdownMenuItem>
          </>
        )}

        {/* Verification Section */}
        {!isAuditor && showVerifyAction && (
          <>
            <DropdownMenuSeparator />
            {showVerifyAction && onVerify && (
              <DropdownMenuItem onClick={onVerify} data-testid="action-verify">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                <span className="text-green-600 font-medium">Verify & Stamp</span>
              </DropdownMenuItem>
            )}
          </>
        )}

        {/* Document Management - Only for active files, non-auditors */}
        {!isAuditor && isActiveFile && (
          <>
            <DropdownMenuSeparator />
            {/* Amendment path: keep audit reason and ask worker to re-upload */}
            {canRequestReplacement && (
              <DropdownMenuItem
                onClick={inspectionUnavailable ? onRequestReplacement : (onRequestReplacement || onReject)}
                data-testid="action-request-replacement"
              >
                <RotateCcw className="h-4 w-4 mr-2 text-amber-600" />
                <span className="text-amber-600">Request Replacement (Amendment)</span>
              </DropdownMenuItem>
            )}
            {/* Hard reject path: explicit evidence rejection */}
            {canRejectEvidence && (
              <DropdownMenuItem
                onClick={onRejectEvidence || onReject}
                data-testid="action-reject-evidence"
              >
                <X className="h-4 w-4 mr-2 text-red-600" />
                <span className="text-red-600">Reject Evidence</span>
              </DropdownMenuItem>
            )}
            {/* Mark as error - when file shouldn't be here */}
            {canMarkUploadedInError && (
              <DropdownMenuItem onClick={onMarkUploadedInError} data-testid="action-mark-error" className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Remove (Uploaded in Error - Admin Cleanup)
              </DropdownMenuItem>
            )}
          </>
        )}

        {/* Advanced Actions - Hidden in submenu or shown sparingly */}
        {canUseAdvancedMutations && (
          <>
            <DropdownMenuSeparator />
            {onMoveCategory && (
              <DropdownMenuItem onClick={onMoveCategory} data-testid="action-move" className="text-gray-600">
                <ArrowRight className="h-4 w-4 mr-2" />
                Move to Category
              </DropdownMenuItem>
            )}
            {onSupersede && (
              <DropdownMenuItem onClick={onSupersede} data-testid="action-supersede" className="text-gray-600">
                <RefreshCw className="h-4 w-4 mr-2" />
                Archive & Replace
              </DropdownMenuItem>
            )}
          </>
        )}

        {/* History - Always available */}
        {onViewHistory && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onViewHistory} data-testid="action-history">
              <History className="h-4 w-4 mr-2" />
              File History
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
